import { useContext, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CheckoutContext from '../lib/CheckoutContext';
import { cartContainsOnlyFreeTrialBundles, isBundleCartItem, skusForCartItem } from '../lib/cart';
import { cartContainsFreeShippingProduct } from '../lib/freeShipping';
import { cartHasShippingOnlyPartnerOffer } from '../lib/shippingOnlyPartnerOffer';
import useShippingOnlyPartnerParentCoupons from './useShippingOnlyPartnerParentCoupons';
import * as types from '../types';
import {
  FLAT_RATE_SHIPPING_CODE,
  FREE_SHIPPING_CODE,
  FREE_SHIPPING_THRESHOLD_BY_CURRENCY,
  FREE_TRIAL_SHIPPING_CODE,
} from '../types/Product';
import LocaleContext, { FiCountry } from '../contexts/LocaleContext';
import { getProductsBySku } from '../lib/product';
import { getLocalizedPricing } from '../lib/util/money';
import useHardwareFirstCartUnits from './useHardwareFirstCartUnits';
import useProductSkuMap from './useProductSkuMap';

export interface UseShippingOptionsResult {
  shippingOptions: types.IShippingOption[];
  /**
   * The default shipping code to use based on the cart contents.
   * For free trial carts, this will be FREE_TRIAL_SHIPPING_CODE.
   * For regular carts, this will be FREE_SHIPPING_CODE.
   */
  defaultShippingCode: types.ShippingCode;
}

export default function useShippingOptions(): UseShippingOptionsResult {
  const dispatch = useDispatch();
  const { country, currency } = useContext(LocaleContext);

  const allShippingOptions = useSelector((state: types.AppState) => state.config.shippingOptions);
  const products = useSelector((state: types.AppState) => state.config.products);
  const disableExpeditedShippingSkus = useSelector(
    (state: types.AppState) => state.config.siteConfig.disableExpeditedShippingSkus || [],
  );
  const { cart, checkoutActions, checkoutState } = useContext(CheckoutContext);
  const productSkuMap = useProductSkuMap();
  const shippingOnlyPartnerParentCoupons = useShippingOnlyPartnerParentCoupons();

  // Check if cart contains ONLY free trial subscriptions
  const hasOnlyFreeTrialBundles = useMemo(() => cartContainsOnlyFreeTrialBundles(cart, products), [cart, products]);
  // partner offers whose coupon covers everything but shipping are charged the free trial fee, like web enforces
  const usesFreeTrialShipping =
    hasOnlyFreeTrialBundles || cartHasShippingOnlyPartnerOffer(cart, productSkuMap, shippingOnlyPartnerParentCoupons);

  // Calculate pre-discount product subtotal to determine free shipping eligibility.
  // For bundles (collar kit + subscription), sum hardware + subscription + addon prices.
  const productSubtotalInCents = useMemo(() => {
    const productsBySku = getProductsBySku(products);
    return Object.values(cart.cartItems).reduce((total, cartItem) => {
      let itemTotal = 0;
      const entry = productSkuMap.get(cartItem.lineItem.sku);
      if (entry?.product?.pricingInCents) {
        itemTotal += getLocalizedPricing(entry.product.pricingInCents, currency);
      }
      if (isBundleCartItem(cartItem)) {
        const subProduct = productsBySku.get(cartItem.subscriptionLineItem.sku);
        if (subProduct?.pricingInCents) {
          itemTotal += getLocalizedPricing(subProduct.pricingInCents, currency);
        }
        for (const addon of cartItem.addonLineItems ?? []) {
          const addonProduct = productsBySku.get(addon.sku);
          if (addonProduct?.pricingInCents) {
            itemTotal += getLocalizedPricing(addonProduct.pricingInCents, currency);
          }
        }
      }
      return total + itemTotal * cartItem.quantity;
    }, 0);
  }, [cart.cartItems, products, productSkuMap, currency]);

  const containsFreeShippingProduct = useMemo(() => cartContainsFreeShippingProduct(cart, products), [cart, products]);

  // hardware-first carts always ship free regardless of the threshold, mirroring the server's
  // forced free ground shipping (a single discounted collar sits under the threshold)
  const isHardwareFirstCart = useHardwareFirstCartUnits() > 0;

  // non-throwing lookup with USD fallback, matching how web's ConsumerStoreCart consumes this
  // constant - getLocalizedPricing throws for a currency missing from the map
  const freeShippingThresholdInCents =
    FREE_SHIPPING_THRESHOLD_BY_CURRENCY[currency] ?? FREE_SHIPPING_THRESHOLD_BY_CURRENCY.USD;
  const qualifiesForFreeShipping =
    containsFreeShippingProduct || isHardwareFirstCart || productSubtotalInCents >= freeShippingThresholdInCents;

  // Check if the flat rate shipping option exists in the config (feature is enabled on backend)
  const hasFlatRateOption = allShippingOptions.some((so) => so.code === FLAT_RATE_SHIPPING_CODE);

  const availableShippingOptions = useMemo(() => {
    // Check if cart contains any SKUs that disable expedited shipping
    const hasSkuThatDisablesExpeditedShipping = Object.values(cart.cartItems).some((cartItem) => {
      const skus = skusForCartItem(cartItem);
      return skus.some((sku) => disableExpeditedShippingSkus.includes(sku));
    });

    let hasCountryThatDisablesExpeditedShipping: boolean;
    switch (country) {
      case FiCountry.US:
        hasCountryThatDisablesExpeditedShipping = false;
        break;
      case FiCountry.CA:
      case FiCountry.GB:
      default:
        hasCountryThatDisablesExpeditedShipping = true;
        break;
    }

    return allShippingOptions.filter((so) => {
      if (so.inPersonEventOnly) {
        return false;
      }

      // Filter out expedited options if cart contains SKUs that disable them
      if (hasSkuThatDisablesExpeditedShipping && so.isExpedited) {
        return false;
      }

      if (hasCountryThatDisablesExpeditedShipping && so.isExpedited) {
        return false;
      }

      if (so.code === FREE_TRIAL_SHIPPING_CODE && !usesFreeTrialShipping) {
        return false;
      }

      if (so.code === FREE_SHIPPING_CODE && usesFreeTrialShipping) {
        return false;
      }

      // When flat rate shipping is available, hide free ground shipping for sub-threshold carts
      if (hasFlatRateOption && so.code === FREE_SHIPPING_CODE && !qualifiesForFreeShipping) {
        return false;
      }

      // Hide flat rate shipping when cart qualifies for free shipping
      if (so.code === FLAT_RATE_SHIPPING_CODE && qualifiesForFreeShipping) {
        return false;
      }

      return true;
    });
  }, [
    allShippingOptions,
    cart,
    usesFreeTrialShipping,
    disableExpeditedShippingSkus,
    country,
    hasFlatRateOption,
    qualifiesForFreeShipping,
  ]);

  // Determine the default shipping code based on cart contents
  const defaultShippingCode: types.ShippingCode = usesFreeTrialShipping
    ? FREE_TRIAL_SHIPPING_CODE
    : hasFlatRateOption && !qualifiesForFreeShipping
      ? FLAT_RATE_SHIPPING_CODE
      : FREE_SHIPPING_CODE;

  useEffect(() => {
    // Make sure the default shippingCode is in the available shipping options
    const defaultShippingOption = availableShippingOptions.find((so) => so.code === defaultShippingCode);

    // If we don't have a saved shipping code, set the default shipping option
    if (!checkoutState.shippingCode) {
      dispatch(checkoutActions.setShippingCode({ shippingCode: defaultShippingOption?.code ?? null }));
      return;
    }

    // If the saved shipping option is no longer available, reset to default
    const hasInvalidShippingCode = !availableShippingOptions.map((so) => so.code).includes(checkoutState.shippingCode);
    if (hasInvalidShippingCode) {
      dispatch(checkoutActions.setShippingCode({ shippingCode: defaultShippingOption?.code ?? null }));
    }
  });

  return {
    shippingOptions: availableShippingOptions,
    defaultShippingCode,
  };
}
